import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface ListLocationState {
  listSearch?: string;
}

/**
 * Used on LIST pages: navigate into a detail/edit/create page, remembering the
 * current list query string so the detail page can return to it.
 */
export function useListNavigate() {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (to: string) => navigate(to, { state: { listSearch: location.search } }),
    [navigate, location.search],
  );
}

/**
 * Used on DETAIL/edit/create pages and their forms: go back to the list,
 * restoring the remembered query string (falls back to the bare list path).
 */
export function useListReturn(listPath: string) {
  const navigate = useNavigate();
  const location = useLocation();
  const listSearch = (location.state as ListLocationState | null)?.listSearch ?? '';

  const goToList = useCallback(() => {
    navigate({ pathname: listPath, search: listSearch });
  }, [navigate, listPath, listSearch]);

  // Forward the same state when hopping detail -> detail (e.g. View -> Edit)
  const listState: ListLocationState = { listSearch };

  return { goToList, listState };
}
